<script lang="ts" setup>
import SvgLoader from '@/utils/SvgLoader.vue';
import { onMounted, onUnmounted, reactive } from 'vue';
import { i18n } from '@/i18n';
import { NotificationMenuItem } from './interfaces/notificationMenuItem';

const t = i18n.t;

const emits = defineEmits<{
  (e: 'deleteTargetRequestId', requestId: string): void;
}>();

const props = defineProps<{
  notificationItems: NotificationMenuItem[];
}>();

const state = reactive({
  isOpen: false,
});

const toggleDropDown = () => {
  state.isOpen = !state.isOpen;
};

/**
 * ドロップダウンを閉じる
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!(event.target as Element).closest(`#notification-menu-popover-dropdown`)) {
    state.isOpen = false;
  }
};

const onClickMenuItem = (item: NotificationMenuItem) => {
  emits('deleteTargetRequestId', item.requestId);
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div id="notification-menu-popover-dropdown" class="notification-menu-container">
    <div class="notification-menu-container__icon" @click="toggleDropDown">
      <SvgLoader name="person" />
    </div>
    <div v-if="state.isOpen" class="notification-menu-container__popover-dropdown popover-dropdown">
      <div class="popover-dropdown__content">
        <div class="popover-dropdown__body dropdown-body">
          <template v-if="notificationItems.length > 0">
            <div
              v-for="(item, index) in notificationItems"
              :key="`notification-item-${index}`"
              class="dropdown-body__content-item-container"
            >
              <div class="dropdown-body__content-item">
                {{ t(item.status) }}
              </div>
              <div class="dropdown-body__delete-icon" @click="onClickMenuItem(item)">
                {{ 'X' }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="dropdown-body__content-item-container">
              <div class="dropdown-body__content-item">
                {{ t('通知情報はありません') }}
              </div>
              <div
                class="dropdown-body__delete-icon"
                @click="onClickMenuItem({ requestId: '', status: '通知情報はありません', actionName: 'non' })"
              >
                {{ 'X' }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notification-menu-container {
  position: relative;

  &__icon {
    width: 40px;
    height: 40px;
    background-color: $semanticPrimary;
    border-radius: 50%;
    display: grid;
    place-items: center;
    &:hover {
      cursor: pointer;
    }
  }

  &__popover-dropdown {
    position: absolute;
    top: 0;
    left: calc(100% + 10px);
    width: 200px;
    max-height: 200px;
    z-index: 1000;
  }
}

.popover-dropdown {
  background-color: $semanticWhite;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  overflow-y: scroll;
  font: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  color: $textDefault;
  text-align: left;
  overflow-wrap: break-word;

  &__content {
    display: grid;
    grid-auto-rows: max-content;
    gap: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
    max-width: 200px;
  }

  &__header {
    width: 100%;
  }

  &__body {
    width: 100%;
  }
}

.dropdown-header {
  border-bottom: 1px solid $borderDefault;
  &__content {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    justify-content: start;
    align-items: center;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 24px;
    padding-right: 16px;
    gap: 8px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    background-color: $semanticPrimary;
    border-radius: 50%;
    display: grid;
    place-items: center;
  }
}

.dropdown-body {
  display: grid;
  grid-auto-rows: minmax(36px, max-content);

  &__content-item-container {
    display: grid;
    grid-template-columns: 1fr 24px;
    &:hover {
      cursor: pointer;
    }
  }

  &__content-item {
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
    padding: 8px;
    gap: 4px;
  }

  &__delete-icon {
    display: grid;
    grid-auto-flow: column;
    justify-content: center;
    align-items: center;
    padding: 8px;
    &:hover {
      cursor: pointer;
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
  }

  &__label-text {
    grid-column: 2 / 3;
  }
}
</style>
